import * as Sentry from '@sentry/nextjs';
import { Component, ErrorInfo, ReactNode } from 'react';

type Props = {
  fallback?: ReactNode | null;
  children?: ReactNode;
};

type State = {
  hasError: boolean;
};

/**
 * ErrorBoundary is a React component that catches errors anywhere in its child component tree, on the client side.
 * If it catches an error, it renders the fallback instead of it's children.
 * Component lifecycle methods do not run on the server render, therefore ErrorBoundary will not catch errors from the server render of a page.
 *
 * @see {@link https://react.dev/reference/react/Component#catching-rendering-errors-with-an-error-boundary}
 */
class ErrorBoundary extends Component<Props, State> {
  state: State = {
    hasError: false,
  };

  static getDerivedStateFromError(): State {
    // Update state so the next render will show the fallback UI.
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught error:', error, errorInfo);
    Sentry.captureException(error);
  }

  render() {
    if (this.state.hasError) {
      // You can render any custom fallback UI
      return this.props.fallback;
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
